import React from "react";
import { Link } from "react-router-dom";
import {
  FileText,
  UserPlus,
  ShieldCheck,
  ArrowRight,
  Lock,
} from "lucide-react";
import Header from "../components/Header";
import Footer from "../components/Footer";

const Card = ({
  icon: Icon,
  iconBg,
  kicker,
  title,
  description,
  primaryLabel = "Continue",
  primaryTo,
  secondaryLabel,
  secondaryTo,
  ambientClass = "card-ambient-gold",
  delayClass = "",
}) => {
  return (
    <div
      className={`card-ambient-gold ${ambientClass} ${delayClass} fade-up rounded-2xl border border-slate-200/80 shadow-sm hover:shadow-md transition-shadow p-6 flex flex-col`}
    >
      <div
        className="w-12 h-12 rounded-xl flex items-center justify-center shadow-sm"
        style={{ background: iconBg }}
      >
        <Icon className="w-6 h-6 text-white" />
      </div>
      <div className="mt-4 text-[11px] font-semibold tracking-[0.18em] uppercase text-slate-500">
        {kicker}
      </div>
      <h3 className="mt-1.5 font-display text-xl font-semibold text-cct-blue">
        {title}
      </h3>
      <p className="mt-2 text-[13px] text-slate-600 leading-relaxed flex-1">
        {description}
      </p>
      <div className="mt-4 flex flex-col gap-1.5">
        <Link
          to={primaryTo}
          className="inline-flex items-center gap-1.5 text-sm font-semibold text-[#C49A3A] hover:text-[#A97F26] transition-colors group"
        >
          {primaryLabel}
          <ArrowRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" />
        </Link>
        {secondaryLabel && (
          <Link
            to={secondaryTo}
            className="inline-flex items-center text-[11px] font-medium text-slate-500 hover:text-cct-blue underline underline-offset-4 decoration-dotted"
          >
            {secondaryLabel} →
          </Link>
        )}
      </div>
    </div>
  );
};

export default function Home() {
  return (
    <div className="min-h-screen bg-cct-ambient flex flex-col">
      <Header />
      <main className="flex-1 max-w-7xl w-full mx-auto px-4 sm:px-6 lg:px-8 pt-2 pb-8">
        <div className="text-center fade-up">
          <div className="inline-flex items-center px-3.5 py-1 rounded-full border border-cct-blue/25 text-[10px] tracking-[0.22em] uppercase text-cct-blue font-semibold bg-white/50 backdrop-blur-sm">
            Welcome to the Portal
          </div>
          <h1 className="mt-3 font-display text-4xl sm:text-5xl md:text-6xl font-semibold text-cct-blue leading-[1.05]">
            Start Your Journey
            <br />
            with <span className="text-gold italic">Cambridge</span>
          </h1>
          <p className="mt-3 max-w-xl mx-auto text-sm text-slate-600 leading-relaxed">
            Choose an option below to continue. Students can apply, partners
            can register, and staff can sign in to the admin console.
          </p>
        </div>

        <div className="mt-7 grid grid-cols-1 md:grid-cols-3 gap-5">
          <Card
            icon={FileText}
            iconBg="#D4A017"
            kicker="For Students"
            title="Apply Now"
            description="Start a new student application. Takes less than 2 minutes to submit your details and documents."
            primaryTo="/apply"
            secondaryLabel="Domestic application form"
            secondaryTo="/apply/domestic"
            delayClass="fade-up-1"
          />
          <Card
            icon={UserPlus}
            iconBg="#1E3A8A"
            kicker="For New Partners"
            title="Recruiter Sign Up"
            description="Join our partner network. Submit your ID and business license for approval."
            primaryTo="/recruiter-signup"
            secondaryLabel="Already a partner? Sign in"
            secondaryTo="/recruiter/login"
            ambientClass="card-ambient-blue"
            delayClass="fade-up-2"
          />
          <Card
            icon={ShieldCheck}
            iconBg="#1E3A8A"
            kicker="For Staff"
            title="Admin Login"
            description="Authorized staff access to manage applications, partners and student records."
            primaryTo="/admin-login"
            ambientClass="card-ambient-blue"
            delayClass="fade-up-3"
          />
        </div>

        <div className="mt-6 flex items-center justify-center gap-2 text-xs text-slate-500">
          <Lock className="w-3.5 h-3.5" />
          Secure portal · SSL encrypted · Data handled per privacy policy
        </div>
      </main>
      <Footer />
    </div>
  );
}
